import { API_URL } from '../config/api';
import { captureException as sentryCaptureException, addBreadcrumb } from '../config/sentry';

const CLIENT_ERROR_ENDPOINT = `${API_URL}/api/client-errors`;
let handlersInstalled = false;

function readStoredUser() {
  try {
    const raw = sessionStorage.getItem('user') || localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

function readStoredToken() {
  try {
    return sessionStorage.getItem('token') || localStorage.getItem('token') || null;
  } catch (_) {
    return null;
  }
}

function getSessionIdFromLocation() {
  if (typeof window === 'undefined') return null;
  const match = window.location.pathname.match(/\/session\/([^/]+)/);
  return match ? match[1] : null;
}

function serializeError(error) {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack || null
    };
  }

  return {
    name: 'NonError',
    message: typeof error === 'string' ? error : JSON.stringify(error),
    stack: null
  };
}

export function buildClientErrorContext(extra = {}) {
  const user = readStoredUser();
  return {
    route: typeof window !== 'undefined' ? window.location.pathname : null,
    userId: user?.id || null,
    sessionId: getSessionIdFromLocation(),
    source: extra.source || null,
    filename: typeof extra.filename === 'string' ? extra.filename.split('?')[0].split('#')[0] : null,
    lineno: Number.isFinite(extra.lineno) ? extra.lineno : null,
    colno: Number.isFinite(extra.colno) ? extra.colno : null,
    componentStack: typeof extra.componentStack === 'string' ? extra.componentStack.slice(0, 4000) : null
  };
}

export async function reportClientError(error, context = {}) {
  const fullContext = buildClientErrorContext(context);
  const payload = {
    error: serializeError(error),
    context: fullContext
  };

  console.error('[client-error]', payload);

  // Send to Sentry (if initialized)
  try {
    sentryCaptureException(error instanceof Error ? error : new Error(String(error)), fullContext);
  } catch (_) {
    // Sentry not initialized or failed
  }

  // Also send to our backend endpoint (for redundancy and custom processing)
  try {
    const token = readStoredToken();
    await fetch(CLIENT_ERROR_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify(payload),
      keepalive: true
    });
  } catch (_) {
    // Keep local console visibility even if the report cannot be delivered.
  }
}

// Track user actions as breadcrumbs for debugging
export function trackAction(action, category = 'user', data = {}) {
  try {
    addBreadcrumb(action, category, data);
  } catch (_) {
    // Breadcrumb tracking failed, not critical
  }
}

export function installGlobalErrorHandlers() {
  if (handlersInstalled || typeof window === 'undefined') return;
  handlersInstalled = true;

  window.addEventListener('error', (event) => {
    reportClientError(event.error || new Error(event.message || 'Window error'), {
      source: 'window.error',
      filename: event.filename || null,
      lineno: event.lineno || null,
      colno: event.colno || null
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    reportClientError(event.reason || new Error('Unhandled promise rejection'), {
      source: 'window.unhandledrejection'
    });
  });

  // Track navigation as breadcrumbs
  if (window.navigation) {
    window.navigation.addEventListener('navigate', (event) => {
      trackAction('Navigate', 'navigation', {
        url: event.destination?.url,
        navigationType: event.navigationType
      });
    });
  }
}
